import axios from 'axios';
import { signageDomain } from './domain';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: signageDomain,
  timeout: 30000, // 30 seconds timeout
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor to add auth token and handle loading states
axiosInstance.interceptors.request.use(
  (config) => {
    // Attach auth token from localStorage if available
    try {
      const token = localStorage.getItem('digitalSignageToken');
      if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (_) {
      // ignore storage access issues
    }

    // Add timestamp to prevent caching for GET requests
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle common responses and errors
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }

    return response;
  },
  (error) => {
    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.data || error.message);
    }

    // Handle common error scenarios
    if (error.response) {
      const { status, data } = error.response;

      // Handle authentication errors
      if (status === 401) {
        // Clear any stored auth data
        localStorage.removeItem('user');

        // Only redirect to login if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }

        return Promise.reject({
          ...error,
          message: data?.message || 'Authentication required'
        });
      }

      // Handle authorization errors
      if (status === 403) {
        return Promise.reject({
          ...error,
          message: data?.message || 'Access denied'
        });
      }

      // Handle validation errors
      if (status === 400) {
        return Promise.reject({
          ...error,
          message: data?.message || 'Invalid request'
        });
      }

      // Handle not found errors
      if (status === 404) {
        return Promise.reject({
          ...error,
          message: data?.message || 'Resource not found'
        });
      }

      // Handle server errors
      if (status >= 500) {
        return Promise.reject({
          ...error,
          message: data?.message || 'Server error. Please try again later.'
        });
      }

      // Handle other HTTP errors
      return Promise.reject({
        ...error,
        message: data?.message || `HTTP Error ${status}`
      });
    }

    // Handle network errors
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      return Promise.reject({
        ...error,
        message: 'Network error. Please check your connection.'
      });
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        ...error,
        message: 'Request timeout. Please try again.'
      });
    }

    // Handle other errors
    return Promise.reject({
      ...error,
      message: error.message || 'An unexpected error occurred'
    });
  }
);

// Helper function for file uploads
export const createFormDataRequest = (data, files = []) => {
  const formData = new FormData();

  // Add regular data
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
        formData.append(key, JSON.stringify(data[key]));
      } else if (Array.isArray(data[key])) {
        formData.append(key, JSON.stringify(data[key]));
      } else {
        formData.append(key, data[key]);
      }
    }
  });

  // Add files
  files.forEach((file, index) => {
    formData.append(`file`, file);
  });

  return {
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  };
};

// Helper function for download requests
export const createDownloadRequest = (url, filename) => {
  return axiosInstance({
    url,
    method: 'GET',
    responseType: 'blob'
  }).then(response => {
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  });
};

// Helper functions for common API patterns
export const apiHelpers = {
  // GET request with error handling
  get: async (url, params = {}) => {
    try {
      const response = await axiosInstance.get(url, { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // POST request with error handling
  post: async (url, data = {}) => {
    try {
      const response = await axiosInstance.post(url, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // PUT request with error handling
  put: async (url, data = {}) => {
    try {
      const response = await axiosInstance.put(url, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // DELETE request with error handling
  delete: async (url) => {
    try {
      const response = await axiosInstance.delete(url);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Upload file with progress
  upload: async (url, data, onProgress = null) => {
    try {
      const formDataConfig = createFormDataRequest(data.data, data.files);

      const config = {
        ...formDataConfig.headers,
        onUploadProgress: onProgress ? (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        } : undefined
      };

      const response = await axiosInstance.post(url, formDataConfig.data, {
        headers: config
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

export default axiosInstance;